import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isTr = language === 'tr';

  // Token packages
  const tokenPackages = [
    { tokens: 1, price: 12, popular: false, savings: null as string | null },
    { tokens: 5, price: 50, popular: true, savings: '17%' },
    { tokens: 10, price: 80, popular: false, savings: '33%' },
  ];

  // Token costs for different features
  const tokenCosts = [
    { code: 'A', feature: isTr ? 'Senaryo Analizi' : 'Script Analysis', tokens: 1 },
    { code: 'K', feature: isTr ? 'Karakter Dökümü' : 'Character Breakdown', tokens: 1 },
    { code: 'M', feature: isTr ? 'Mekan Dökümü' : 'Location Breakdown', tokens: 1 },
    { code: 'S', feature: isTr ? 'Sıralı Döküm' : 'Sequential Breakdown', tokens: 4 },
    { code: 'V', feature: isTr ? 'Senaryo Okuma (Ses)' : 'Script Reading (Voice)', tokens: 4 },
  ];

  const handlePurchase = () => {
    const token = localStorage.getItem('access_token');
    if (token) navigate('/payment');
    else navigate('/login');
  };

  const handleContact = () => {
    window.location.href = `mailto:info@mrmirror.ai?subject=${
      isTr ? 'Özel Çözümler' : 'Custom Solutions'
    }`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Section header */}
        <div className="flex items-baseline justify-between mb-16 max-w-5xl mx-auto">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'FİYATLANDIRMA' : 'PRICING'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'TOKEN PAKETLERİ' : 'TOKEN BUNDLES'}</span>
        </div>

        <div className="max-w-3xl mb-20">
          <span className="eyebrow mb-4 block">{isTr ? 'BASİT, ŞEFFAF' : 'SIMPLE, TRANSPARENT'}</span>
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Token. <em>Tek birim,</em> tek hesap.</> : <>Tokens. <em>One unit,</em> one bill.</>}
          </h1>
          <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'İhtiyacın kadar al, kullandığın kadar harca. Kullanılmayan tokenlar bir sonraki aya devreder.'
              : 'Buy as you need, spend as you use. Unused tokens roll over to the next month.'}
          </p>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-24 max-w-5xl mx-auto">
          {tokenPackages.map((pkg, i) => {
            const pricePerToken = (pkg.price / pkg.tokens).toFixed(2);
            return (
              <div
                key={i}
                className={`relative border p-8 transition-colors ${
                  pkg.popular
                    ? 'border-accent bg-accent-soft'
                    : 'border-hairline bg-surface hover:border-accent/60'
                }`}
              >
                <div className="flex items-baseline justify-between mb-6">
                  <span className="eyebrow text-ink-mute">/{String(i + 1).padStart(2, '0')}</span>
                  {pkg.popular ? (
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-accent">
                      ★ {isTr ? 'EN POPÜLER' : 'POPULAR'}
                    </span>
                  ) : pkg.savings ? (
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold text-success">
                      −{pkg.savings}
                    </span>
                  ) : null}
                </div>

                <div className="mb-8">
                  <div className="font-serif italic text-5xl text-ink leading-none mb-2">
                    {pkg.tokens}
                    <span className="font-mono not-italic text-[14px] text-ink-mute ml-2 tracking-wider align-top">
                      {isTr ? 'TOKEN' : 'TOKEN'}{pkg.tokens > 1 ? 'S' : ''}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-3xl font-bold text-ink">${pkg.price}</span>
                    <span className="font-mono text-[11px] tracking-wider uppercase text-ink-mute">
                      · ${pricePerToken}/{isTr ? 'token' : 'token'}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-10 font-prose text-[14px] text-ink-dim leading-relaxed">
                  <li className="flex gap-3">
                    <span className="text-accent font-mono mt-0.5">→</span>
                    <span>{pkg.tokens} {isTr ? 'token kredisi' : 'token credits'}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-accent font-mono mt-0.5">→</span>
                    <span>{isTr ? 'Devreden bakiye, ay sonu kayıp yok' : 'Carry-over balance, no monthly loss'}</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-accent font-mono mt-0.5">→</span>
                    <span>{isTr ? 'Yıl sonunda sıfırlanır' : 'Resets at year end'}</span>
                  </li>
                </ul>

                <button
                  onClick={handlePurchase}
                  className={pkg.popular ? 'btn-accent w-full justify-center' : 'btn-ghost w-full justify-center'}
                >
                  {isTr ? 'SATIN AL' : 'PURCHASE'}
                  <span aria-hidden>→</span>
                </button>
              </div>
            );
          })}
        </div>

        {/* Token Costs */}
        <div className="mb-24 max-w-5xl mx-auto">
          <div className="flex items-baseline justify-between mb-12">
            <span className="eyebrow"><span className="text-accent">/02</span> · {isTr ? 'KULLANIM' : 'CONSUMPTION'}</span>
            <span className="eyebrow-mute hidden md:inline">{isTr ? 'ÖZELLİK BAŞINA' : 'PER FEATURE'}</span>
          </div>

          <div className="max-w-2xl mb-12">
            <h2 className="display-h display-md mb-4">
              {isTr ? <>Her özellik, <em>kendi maliyeti</em>.</> : <>Each feature, <em>its own cost</em>.</>}
            </h2>
            <p className="font-prose text-[15px] leading-relaxed text-ink-dim">
              {isTr
                ? 'Tokenlar, kullandığın özelliğe göre eksilir. Sadece çağırdığın işlem için ödeme yaparsın.'
                : 'Tokens are consumed per feature called. You only pay for what you actually run.'}
            </p>
          </div>

          <div className="border border-hairline">
            {tokenCosts.map((cost, i) => (
              <div
                key={i}
                className={`grid grid-cols-12 items-center px-6 py-5 transition-colors hover:bg-accent-soft ${
                  i < tokenCosts.length - 1 ? 'border-b border-hairline' : ''
                }`}
              >
                <span className="col-span-1 font-mono text-[10px] tracking-[0.2em] uppercase text-accent">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="col-span-1 font-serif italic text-2xl text-accent">
                  {cost.code}
                </span>
                <span className="col-span-7 md:col-span-8 font-prose text-[15px] text-ink">
                  {cost.feature}
                </span>
                <span className="col-span-3 md:col-span-2 text-right font-mono text-[14px] font-bold text-ink tabular-nums">
                  {cost.tokens} <span className="text-ink-mute font-medium">{isTr ? 'TOKEN' : 'TOKEN'}{cost.tokens > 1 ? 'S' : ''}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Solutions */}
        <div className="border border-hairline p-10 md:p-14 mb-12 max-w-5xl mx-auto">
          <span className="eyebrow mb-4 block">{isTr ? 'KURUMSAL' : 'ENTERPRISE'}</span>
          <h2 className="display-h display-md mb-4 max-w-2xl">
            {isTr ? <>Stüdyolar için <em>özel paketler</em>.</> : <>Bespoke packages for <em>studios</em>.</>}
          </h2>
          <p className="font-prose text-[15px] leading-relaxed text-ink-dim mb-8 max-w-2xl">
            {isTr
              ? 'Kurumsal hacim, çoklu kullanıcı, özel SLA. İhtiyacına göre paket düzenliyoruz — bir konuşma yeter.'
              : 'Enterprise volume, multi-seat, custom SLA. We tailor the package — a single conversation is enough.'}
          </p>
          <button onClick={handleContact} className="btn-ghost">
            {isTr ? 'İLETİŞİME GEÇ' : 'GET IN TOUCH'}
            <span aria-hidden>→</span>
          </button>
        </div>

        {/* FAQ teaser */}
        <div className="text-center max-w-2xl mx-auto">
          <span className="eyebrow-mute mb-4 block">{isTr ? 'SORULARIN MI VAR?' : 'QUESTIONS?'}</span>
          <h3 className="display-h display-sm mb-6">
            {isTr ? <>SSS, <em>her şey</em> orada.</> : <>The FAQ has <em>everything</em>.</>}
          </h3>
          <button onClick={() => navigate('/faq')} className="btn-ghost">
            {t.faq}
            <span aria-hidden>→</span>
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
